// Shared persistence utilities for device discovery suppression.
// Stores a simple map of installationID -> ISO timestamp suppressed.

interface DiscoveryPrefsStore {
    suppressedInstallations: Record<string, string>;
}

const KEY = 'deviceDiscovery:v1';

function readStore(): DiscoveryPrefsStore {
    try {
        const raw = window.localStorage.getItem(KEY);
        if (!raw) {
            return { suppressedInstallations: {} };
        }
        const parsed: unknown = JSON.parse(raw);
        if (
            typeof parsed === 'object' &&
            parsed !== null &&
            'suppressedInstallations' in parsed &&
            typeof (parsed as Record<string, unknown>).suppressedInstallations === 'object'
        ) {
            const s = (parsed as { suppressedInstallations: unknown }).suppressedInstallations;
            if (s && typeof s === 'object') {
                return { suppressedInstallations: { ...(s as Record<string, string>) } };
            }
        }
        return { suppressedInstallations: {} };
    } catch {
        return { suppressedInstallations: {} };
    }
}

function writeStore(store: DiscoveryPrefsStore) {
    try {
        window.localStorage.setItem(KEY, JSON.stringify(store));
    } catch (error) {
        console.warn('deviceDiscovery persistence failed', error);
    }
}

export function suppressInstallation(installationID: string) {
    if (!installationID) {
        return;
    }
    const store = readStore();
    store.suppressedInstallations[installationID] = new Date().toISOString();
    writeStore(store);
}

export function unsuppressInstallation(installationID: string) {
    if (!installationID) {
        return;
    }
    const store = readStore();
    if (installationID in store.suppressedInstallations) {
        const { [installationID]: _removed, ...rest } = store.suppressedInstallations;
        store.suppressedInstallations = rest;
        writeStore(store);
    }
}

export function isSuppressed(installationID: string | null | undefined): boolean {
    if (!installationID) {
        return false;
    }
    const store = readStore();
    return !!store.suppressedInstallations[installationID];
}

export function _debugReadDiscoveryPrefs(): DiscoveryPrefsStore {
    // optional debug export
    return readStore();
}
